import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc, collection, addDoc, deleteDoc, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';

/**
 * Crée un compte administrateur avec authentification Firebase
 * @param {Object} adminData - Données de l'administrateur
 * @param {string} adminData.email - Email de l'administrateur
 * @param {string} adminData.password - Mot de passe de l'administrateur
 * @param {string} adminData.name - Nom de l'administrateur
 * @param {string} adminData.position - Poste de l'administrateur
 * @param {string} adminData.requestId - ID de la demande d'administrateur
 * @param {string} approvedBy - ID de l'administrateur qui approuve
 * @returns {Promise<Object>} Résultat de l'opération
 */
export const createAdminWithAuth = async (adminData, approvedBy) => {
  const { email, password, name, position, requestId } = adminData;
  
  try {
    // Vérifier si l'email existe déjà dans Firebase Authentication
    // Cette étape est importante pour éviter les erreurs d'authentification
    const adminQuery = await getAdminCount();
    
    // Si c'est le premier administrateur, lui donner le rôle de PDG
    // Sinon, lui donner le rôle d'admin standard
    const role = adminQuery === 0 ? 'PDG' : 'admin';
    
    // Vérifier si un utilisateur avec cet email existe déjà dans Firestore
    const emailQuery = query(collection(db, 'users'), where('email', '==', email));
    const emailSnapshot = await getDocs(emailQuery);
    
    let newAdminId;
    
    if (!emailSnapshot.empty) {
      // L'utilisateur existe déjà dans Firestore mais pas dans Authentication
      console.log('Utilisateur trouvé dans Firestore mais pas dans Authentication');
      newAdminId = emailSnapshot.docs[0].id;
      
      // On essaie de créer le compte d'authentification
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        // Mettre à jour l'ID si nécessaire
        if (userCredential.user.uid !== newAdminId) {
          // Copier les données vers le nouvel ID et supprimer l'ancien
          const oldData = emailSnapshot.docs[0].data();
          await setDoc(doc(db, 'users', userCredential.user.uid), oldData);
          await deleteDoc(doc(db, 'users', newAdminId));
          newAdminId = userCredential.user.uid;
        }
      } catch (authError) {
        console.error('Erreur lors de la création du compte d\'authentification:', authError);
        throw authError; // Propager l'erreur pour la gestion dans le bloc catch principal
      }
    } else {
      // Créer un compte d'authentification Firebase pour l'administrateur
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      newAdminId = userCredential.user.uid;
      
      // Enregistrer les informations dans Firestore avec le rôle approprié
      await setDoc(doc(db, 'users', newAdminId), {
        name,
        email,
        role,
        position,
        createdAt: new Date().toISOString(),
        isEmailVerified: false,
        pendingRegistration: false
      });
    }
    
    // Créer une entrée dans la collection admin_approvals pour la traçabilité
    const approvalRef = doc(collection(db, 'admin_approvals'));
    const approvalData = {
      adminId: newAdminId,
      adminEmail: email,
      adminName: name,
      position,
      approvedBy,
      approvedAt: new Date().toISOString(),
      originalRequestId: requestId,
      isFirstAdmin: adminQuery === 0
    };
    
    // Créer le document d'approbation
    await setDoc(approvalRef, approvalData);
    
    // Supprimer la demande de la collection admin_requests
    if (requestId) {
      await deleteDoc(doc(db, 'admin_requests', requestId));
    }
    
    return {
      success: true,
      adminId: newAdminId,
      role,
      message: `L'administrateur ${name} a été créé avec succès.`
    };
  } catch (error) {
    console.error('Erreur lors de la création de l\'administrateur:', error);
    
    // Gestion des erreurs spécifiques
    let errorMessage = 'Une erreur est survenue lors de la création de l\'administrateur.';
    
    if (error.code === 'auth/email-already-in-use') {
      errorMessage = 'Cet email est déjà utilisé par un compte existant. Veuillez contacter l\'administrateur système.';
      
      // Tentative de récupération: vérifier si l'utilisateur existe dans Firestore mais pas dans Auth
      try {
        const emailQuery = query(collection(db, 'users'), where('email', '==', email));
        const emailSnapshot = await getDocs(emailQuery);
        
        if (!emailSnapshot.empty) {
          errorMessage = 'Cet utilisateur existe dans la base de données mais pas dans le système d\'authentification. Veuillez contacter l\'administrateur système.';
        }
      } catch (subError) {
        console.error('Erreur lors de la vérification supplémentaire:', subError);
      }
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'L\'adresse email est invalide.';
    } else if (error.code === 'auth/weak-password') {
      errorMessage = 'Le mot de passe est trop faible. Il doit contenir au moins 6 caractères.';
    } else if (error.code === 'auth/invalid-credential') {
      errorMessage = 'Identifiants invalides. Veuillez vérifier l\'email et le mot de passe.';
    } else if (error.code === 'auth/operation-not-allowed') {
      errorMessage = 'La création de compte est désactivée. Veuillez contacter l\'administrateur système.';
    } else if (error.code === 'permission-denied') {
      errorMessage = 'Accès refusé. Vous n\'avez pas les permissions nécessaires.';
    }
    
    return {
      success: false,
      error: errorMessage,
      code: error.code
    };
  }
};

/**
 * Récupère le nombre d'administrateurs (admin + PDG)
 * @returns {Promise<number>} Nombre d'administrateurs
 */
export const getAdminCount = async () => {
  try {
    const adminSnapshot = await getDoc(doc(db, 'statistics', 'admin_count'));
    if (adminSnapshot.exists()) {
      return adminSnapshot.data().count || 0;
    }
    
    // Si le document n'existe pas, compter manuellement
    const adminQuery = query(collection(db, 'users'), where('role', 'in', ['admin', 'PDG']));
    const adminDocs = await getDocs(adminQuery);
    return adminDocs.size;
  } catch (error) {
    console.error('Erreur lors de la récupération du nombre d\'administrateurs:', error);
    return 0;
  }
};